
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

// Database storage limits per user type
const STORAGE_LIMITS = {
  free: 25 * 1024 * 1024, // 25MB for free users
  premium: 100 * 1024 * 1024, // 100MB for premium users
};

// Estimate storage used by user data with timeout
export async function calculateUserStorageUsage() {
  if (!auth.currentUser) return 0;

  try {
    // Add timeout to prevent hanging on database calls
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('USER_DOC_TIMEOUT')), 3000)
    );
    
    const docPromise = getDoc(doc(db, 'users', auth.currentUser.uid));
    const userDoc = await Promise.race([docPromise, timeoutPromise]);
    
    const userData = userDoc.data();
    
    let totalSize = 0;
    
    // Calculate storage from user profile data
    if (userData) {
      totalSize += JSON.stringify(userData).length * 2; // UTF-16 encoding
    }

    // Note: Wishlist data calculation is handled separately in storageUtils.js
    // For now, return a conservative estimate to prevent blocking uploads
    return Math.min(totalSize, 1024 * 1024); // Cap at 1MB to prevent false positives
  } catch (error) {
    console.error('Error calculating storage usage:', error);
    if (error.message === 'USER_DOC_TIMEOUT') {
      console.warn('⚠️ User document fetch timed out, returning minimal usage');
    }
    return 0; // Return 0 on error to allow operations to proceed
  }
}

export async function checkDatabaseStorageLimit(isPremium = false) {
  const limit = isPremium ? STORAGE_LIMITS.premium : STORAGE_LIMITS.free;
  const currentUsage = await calculateUserStorageUsage();
  
  return {
    isAtLimit: currentUsage >= limit,
    isNearLimit: currentUsage >= (limit * 0.8), // 80% threshold
    currentUsage,
    limit,
    remainingStorage: Math.max(0, limit - currentUsage)
  };
}

export function formatStorageSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
